import React, { useEffect, useState } from 'react';
import { helpElement } from '../../help/attrs';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { PettyCashService } from '../../services/pettyCash.service';
import { PettyCashFund } from '../../interfaces/treasury.interface';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { SearchableSelect } from '../common/SearchableSelect';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';
import { saveBlob } from '../../utils/saveBlob';
import { MAX_ATTACHMENT_BYTES } from '../common/AttachmentsPanel';
import { segmentLabel } from './treasuryRef';
import DepartmentPicker, { useDepartmentOptions } from '../costcentre/DepartmentPicker'; // B4-H
import ProjectSelect, { useProjectOptions } from '../project/ProjectSelect'; // P1-A project
import { AuthService } from '../../services/auth.service'; // B4-H

interface VoucherDialogProps {
  open: boolean;
  funds: PettyCashFund[];
  fundId?: string;
  expenses: Account[];
  segments: Segment[];
  onClose: () => void;
  onSaved: () => void;
}

/** Add a petty-cash voucher (posts at save) with an optional receipt file. Shared by admin and custodian pages. */
export const VoucherDialog: React.FC<VoucherDialogProps> = ({ open, funds, fundId, expenses, segments, onClose, onSaved }) => {
  const toast = useToast();
  const [fund, setFund] = useState('');
  const [date, setDate] = useState(toLocalIsoDate());
  const [expenseId, setExpenseId] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [departmentId, setDepartmentId] = useState(''); // B4-H
  const departmentOptions = useDepartmentOptions(AuthService.getSelectedInstitution()); // B4-H
  const [projectId, setProjectId] = useState(''); // P1-A project
  const projectOptions = useProjectOptions(AuthService.getSelectedInstitution()); // P1-A project
  const [amount, setAmount] = useState('');
  const [payee, setPayee] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFund(fundId ?? (funds.length === 1 ? funds[0].id : '')); setDate(toLocalIsoDate()); setExpenseId(''); setSegmentId(''); setDepartmentId(''); setProjectId('');
      setAmount(''); setPayee(''); setDescription(''); setFile(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = funds.find(f => f.id === fund);
  const overLimit = !!selected && Number(amount) > selected.perVoucherLimit;
  const overBalance = !!selected && Number(amount) > selected.glBalance;
  const tooBig = !!file && file.size > MAX_ATTACHMENT_BYTES;
  const valid = !!fund && !!expenseId && Number(amount) > 0 && !overLimit && !overBalance && !tooBig;

  const save = async () => {
    try {
      setSaving(true);
      const v = await PettyCashService.createVoucher({
        fundId: fund, voucherDate: date || undefined, expenseAccountId: expenseId, segmentId: segmentId || undefined, departmentId: departmentId || undefined, projectId: projectId || undefined,
        amount: Number(amount), payee: payee.trim() || undefined, description: description.trim() || undefined,
      });
      if (file) {
        try {
          await PettyCashService.uploadReceipt(v.id, file);
        } catch (e) {
          toast.showApiError(e, 'Voucher saved, but the receipt upload failed');
        }
      }
      toast.showSuccess('Voucher saved');
      onSaved();
    } catch (e) {
      toast.showApiError(e, 'Failed to save voucher');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'bank.pcVoucher.page' } as any}>
      <DialogTitle>New petty-cash voucher</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.pcVoucher.section.body">
        <SearchableSelect<PettyCashFund> data-help="bank.pcVoucher.field.fund" label="Fund" options={funds.filter(f => f.status === 'ACTIVE')} value={fund} required
          getOptionLabel={f => f.name} onChange={id => setFund(id)} />
        {selected && (
          <Typography variant="body2" color="text.secondary">
            Balance {formatMoney(selected.glBalance)} - limit per voucher {formatMoney(selected.perVoucherLimit)}
          </Typography>
        )}
        <TextField data-help="bank.pcVoucher.field.date" label="Voucher date" type="date" value={date} fullWidth InputLabelProps={{ shrink: true }}
          inputProps={{ max: toLocalIsoDate() }} onChange={e => setDate(e.target.value)} />
        <AccountAutocomplete data-help="bank.pcVoucher.field.expense" accounts={expenses} value={expenseId} label="Expense account" required onChange={id => setExpenseId(id)} />
        <SearchableSelect<Segment> data-help="bank.pcVoucher.field.segment" label="Segment" options={segments} value={segmentId} getOptionLabel={segmentLabel} onChange={id => setSegmentId(id)} />
        {departmentOptions.length > 0 && ( // B4-H
          <DepartmentPicker data-help="bank.pcVoucher.field.department" options={departmentOptions} value={departmentId} label="Department" onChange={id => setDepartmentId(id)} />
        )}
        {projectOptions.length > 0 && ( // P1-A project
          <ProjectSelect data-help="bank.pcVoucher.field.project" options={projectOptions} value={projectId} label="Project" onChange={id => setProjectId(id)} data-testid="voucher-project-select" />
        )}
        <TextField data-help="bank.pcVoucher.field.amount" label="Amount" type="number" value={amount} required fullWidth error={overLimit || overBalance}
          helperText={overLimit ? 'Above the per-voucher limit' : overBalance ? 'Above the fund balance' : ''}
          onChange={e => setAmount(e.target.value)} />
        <TextField data-help="bank.pcVoucher.field.payee" label="Payee" value={payee} fullWidth onChange={e => setPayee(e.target.value)} />
        <TextField data-help="bank.pcVoucher.field.description" label="Description" value={description} fullWidth multiline minRows={2} onChange={e => setDescription(e.target.value)} />
        <Button data-help="bank.pcVoucher.field.receipt" component="label" variant="outlined">
          {file ? file.name : 'Attach receipt (optional)'}
          <input hidden type="file" data-testid="receipt-input" onChange={e => setFile(e.target.files?.[0] ?? null)} />
        </Button>
        {tooBig && <Typography variant="caption" color="error">Receipt is over 5 MB.</Typography>}
      </DialogContent>
      <DialogActions>
        <Button data-help="bank.pcVoucher.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="bank.pcVoucher.save" variant="contained" disabled={saving || !valid} onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

/** Lists a voucher's receipts as download buttons and offers an upload. */
export const ReceiptButtons: React.FC<{ voucherId: string; canUpload: boolean }> = ({ voucherId, canUpload }) => {
  const toast = useToast();
  const [items, setItems] = useState<{ id: string; fileName: string }[] | null>(null);

  const load = async () => {
    try {
      setItems(await PettyCashService.listReceipts(voucherId));
    } catch (e) {
      toast.showApiError(e, 'Failed to load receipts');
    }
  };
  const download = async (id: string, name: string) => {
    try {
      saveBlob(await PettyCashService.downloadReceipt(voucherId, id), name);
    } catch (e) {
      toast.showApiError(e, 'Download failed');
    }
  };
  const upload = async (f: File | undefined) => {
    if (!f) return;
    try {
      await PettyCashService.uploadReceipt(voucherId, f);
      toast.showSuccess('Receipt uploaded');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Upload failed');
    }
  };

  return (
    <>
      {items === null
        ? <Button data-help="bank.pcReceipt.list" size="small" onClick={load}>Receipts</Button>
        : items.map(a => <Button data-help="bank.pcReceipt.download" key={a.id} size="small" onClick={() => download(a.id, a.fileName)}>{a.fileName}</Button>)}
      {canUpload && (
        <Button data-help="bank.pcReceipt.upload" size="small" component="label">
          Upload receipt
          <input hidden type="file" onChange={e => upload(e.target.files?.[0])} />
        </Button>
      )}
    </>
  );
};
